import React from 'react';
import 'dayjs/locale/de';
import PropTypes from 'prop-types';
import {
  Button,
  Typography,
  Stack,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import * as Yup from 'yup';
import { useMemo, useState, useEffect } from 'react';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-mui';
import { useTranslation } from 'react-i18next';
import { validators } from '../../../../services/utils/validators';
import { useUser } from '../../../../services/contexts';
import { useAuth } from '../../../../services/contexts/AuthContext';
import LogoLoader from '../../../../components/LogoLoader';
import api from '../../../../Middleware/api/api';
/**
 * @param {PropTypes.InferProps<UserCreationForm.propTypes>} props
 */
const UserCreationForm = ({
  payload,
  setPayload,
  confirmPayload,
  submit,
  setSubmit,
  setCanNext,
  CreateUpdate,
}) => {
  //let isTest = false;
  const { t } = useTranslation();
  const { user } = useUser();
  const { token } = useAuth();
  const billingType = 'WITH_BILLING';
  const authType = 'APP';
  const [minStartDate, setMinStartDate] = useState('');
  const [isAccessRequested, setIsAccessRequested] = useState(
    payload?.contractStatus === 'ACCESS_REQUESTED' || false
  );

  const createManualEndCustomerIdInitialValue = () => {
    if (CreateUpdate == 'create') {
      return user?.createManualEndCustomerId;
    } else {
      return (
        payload?.endCustomerIdManuallyCreated ||
        (user?.createManualEndCustomerId && isAccessRequested)
      );
    }
  };
  const endCustomerIdIsValidatedInitialValue = () => {
    if (CreateUpdate == 'create') {
      return !user?.createManualEndCustomerId;
    } else {
      return true;
    }
  };

  const [createManualEndCustomerId, setCreateManualEndCustomerId] = useState(
    createManualEndCustomerIdInitialValue()
  );

  const [endCustomerIdIsValidated, setEndCustomerIdIsValidated] = useState(
    endCustomerIdIsValidatedInitialValue()
  );

  const [checkingEndCustomerIdExists, setCheckingEndCustomerIdExists] =
    useState(false);
  const [endCustomerIdExists, setEndCustomerIdExists] = useState(false);

  useEffect(() => {
    if (submit > 0) document.getElementById('sub').click();
    setSubmit(0);
    var today = new Date();
    var yyyy = today.getFullYear();
    setMinStartDate(
      yyyy + today.toLocaleString('en-US', { month: '2-digit' }) + '01'
    );
  }, [submit, minStartDate]);
  const initialValues = {
    firstName: payload?.firstName || '',
    lastName: payload?.lastName || '',
    email: payload?.email || '',
    street: payload?.contractAddress?.street || '',
    careOf: payload?.contractAddress?.careOf || '',
    city: payload?.contractAddress?.city || '',
    zipCode: payload?.contractAddress?.zipCode || '',
    endCustomerId: payload?.endCustomerId || '',
    useCustomBillingDetails: payload?.useCustomBillingDetails || false,
    billingAddressName: payload?.contractBillingAddress?.name || '',
    billingAddressStreet: payload?.contractBillingAddress?.street || '',
    billingAddressCareOf: payload?.contractBillingAddress?.careOf || '',
    billingAddressCity: payload?.contractBillingAddress?.city || '',
    billingAddressZipCode: payload?.contractBillingAddress?.zipCode || '',
    billingEmail: payload?.billingEmail || '',
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        firstName: Yup.string().required(t('firstnamereq')).label(t('vernom')),
        lastName: Yup.string().required(t('lnameReq')).label(t('lastname')),
        email: Yup.string()
          .matches(validators.EMAIL, t('wrongEmail'))
          .email(t('wrongEmail'))
          .required(t('emailReq'))
          .label('E-Mail'),
        billingEmail: Yup.string()
          .matches(validators.EMAIL, t('wrongEmail'))
          .email(t('wrongEmail'))
          .label(t('billingEmail')),
        street: Yup.string()
          .required(t('LBLStreetNameReq'))
          .label(t('lblStName')),
        city: Yup.string().required(t('LBLLocReq')).label(t('city')),
        zipCode: Yup.number()
          .typeError(t('NumericOnly'))
          .required(t('ZipcodeReq'))
          .label(t('PostalCode')),
        careOf: Yup.string().label('c/o'),
        endCustomerId: createManualEndCustomerId
          ? Yup.string()
              .matches(validators.END_CUSTOMER_ID, t('OnlyAlphanumericLBL'))
              .required(t('endCustomerIdReq'))
              .label(t('vernom'))
          : Yup.string().label('endCustomerId'),

        billingAddressName: Yup.string().when('useCustomBillingDetails', {
          is: true,
          then: (schema) => schema.required(t('nameReq')),
          otherwise: (schema) => schema.notRequired(),
        }),
        billingAddressStreet: Yup.string().when('useCustomBillingDetails', {
          is: true,
          then: (schema) => schema.required(t('LBLStreetNameReq')),
          otherwise: (schema) => schema.notRequired(),
        }),
        billingAddressCareOf: Yup.string().label('c/o'),
        billingAddressZipCode: Yup.number().when('useCustomBillingDetails', {
          is: true,
          then: (schema) =>
            schema.typeError(t('NumericOnly')).required(t('ZipcodeReq')),
          otherwise: (schema) => schema.notRequired(),
        }),
        billingAddressCity: Yup.string().when('useCustomBillingDetails', {
          is: true,
          then: (schema) => schema.required(t('LBLLocReq')),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [billingType, authType, createManualEndCustomerId]
  );

  const checkIfEndCustomerIdExists = async (endCustomerId) => {
    let contractDbId = payload?.contractId;
    if (!endCustomerId?.trim()) {
      setEndCustomerIdIsValidated(false);
      return;
    }
    setCheckingEndCustomerIdExists(true);
    try {
      if (CreateUpdate == 'create') {
        const response = await api.endCustomerIdValidationOnCreate(
          token,
          endCustomerId
        );
        const exists = response?.data?.data;
        setEndCustomerIdExists(exists);
        setEndCustomerIdIsValidated(!exists);
        setCheckingEndCustomerIdExists(false);
      } else {
        const response = await api.endCustomerIdValidationOnUpdate(
          token,
          endCustomerId,
          contractDbId
        );
        const exists = response?.data?.data;
        setEndCustomerIdExists(exists);
        setEndCustomerIdIsValidated(!exists);
        setCheckingEndCustomerIdExists(false);
      }
    } catch (err) {
      console.error('Failed checkIfEndCustomerIdExists:', err);
      setEndCustomerIdExists(false);
      setEndCustomerIdIsValidated(false);
      setCheckingEndCustomerIdExists(false);
    }
  };

  /** @type {import("formik").FormikConfig<typeof initialValues>['onSubmit']} */
  const handleSubmit = (values, actions) => {
    const payload = {
      firstName: values?.firstName,
      lastName: values?.lastName,
      email: values?.email,
      contractAddress: {
        city: values?.city,
        careOf: values?.careOf,
        street: values?.street,
        zipCode: values?.zipCode,
        countryCode: 'DE', // always DE by default for Germany
      },
      endCustomerId: values?.endCustomerId,
      useCustomBillingDetails: values?.useCustomBillingDetails,
      contractBillingAddress: {
        name: values?.billingAddressName,
        street: values?.billingAddressStreet,
        careOf: values?.billingAddressCareOf,
        city: values?.billingAddressCity,
        zipCode: values?.billingAddressZipCode,
        countryCode: 'DE', // always DE by default for Germany
      },
      billingEmail: values?.billingEmail,
    };

    setPayload(payload);
    confirmPayload();
    actions.setSubmitting(false);
    actions.setTouched({}, false);
    actions.setErrors({});
    actions.setFieldError({});
    actions.setFieldTouched({}, false, false);
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount={true}
      onSubmit={handleSubmit}
    >
      {({ touched, values, setFieldValue, isValid }) => (
        <Form noValidate>
          <>
            <LogoLoader loading={checkingEndCustomerIdExists} />
            {setCanNext(touched && isValid && endCustomerIdIsValidated)}
            {createManualEndCustomerId && (
              <Stack my={5} direction="row" alignItems="center" spacing={2}>
                <Field
                  fullWidth
                  margin="none"
                  component={TextField}
                  onChange={(e) => {
                    setFieldValue('endCustomerId', e.target.value);
                    setEndCustomerIdIsValidated(false);
                  }}
                  id="Kundennummer"
                  name="endCustomerId"
                  label={t('Kundennummer')}
                  required
                  InputProps={{
                    title:
                      'Wenn Sie keine Kundennummer eingeben, wird automatisch eine generiert',
                  }}
                />

                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    color: 'grey.700',
                    whiteSpace: 'nowrap',
                    padding: '4px 10px',
                    width: 'auto',
                    minWidth: 'fit-content',
                    maxWidth: 180,
                  }}
                  color="inherit"
                  onClick={() =>
                    checkIfEndCustomerIdExists(values?.endCustomerId)
                  }
                >
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 'clamp(0.65rem, 1.5vw, 0.9rem)',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {t('ValidateEndCustomerIdBTNLBL')}
                  </Typography>
                </Button>

                <Chip
                  label={
                    endCustomerIdExists
                      ? t('EndCustomerIdExistsLBL')
                      : endCustomerIdIsValidated
                        ? t('EndCustomerIdValidatedLBL')
                        : t('EndCustomerIdNotValidatedLBL')
                  }
                  color={
                    endCustomerIdExists
                      ? 'error'
                      : endCustomerIdIsValidated
                        ? 'success'
                        : 'error'
                  }
                />
              </Stack>
            )}
            <Stack my={5} direction="row" alignItems="center" spacing={8}>
              <Field
                disabled={isAccessRequested}
                fullWidth
                margin="none"
                component={TextField}
                id="vorname"
                name="firstName"
                label={t('vernom')}
                required
                InputProps={{
                  title: 'Bitte füllen Sie dieses Feld aus',
                }}
              />
              <Field
                disabled={isAccessRequested}
                fullWidth
                margin="none"
                component={TextField}
                id="nachname"
                name="lastName"
                label={t('lastname')}
                required
                InputProps={{
                  title: 'Bitte füllen Sie dieses Feld aus',
                }}
              />
            </Stack>
            <Stack mb={5} direction="row" spacing={8}>
              <Field
                disabled={isAccessRequested}
                fullWidth
                margin="none"
                component={TextField}
                id="straße-und-nummer"
                name="street"
                label={t('lblStName')}
                required
                InputProps={{
                  title: 'Bitte füllen Sie dieses Feld aus',
                }}
              />
              <Field
                disabled={isAccessRequested}
                fullWidth
                margin="none"
                component={TextField}
                id="c-o"
                name="careOf"
                label="c/o"
                InputProps={{
                  title: 'Bitte füllen Sie dieses Feld aus',
                }}
              />
            </Stack>
            <Stack mb={5} direction="row" spacing={8}>
              <Field
                disabled={isAccessRequested}
                fullWidth
                required
                margin="none"
                component={TextField}
                id="postleitzahl"
                name="zipCode"
                label={t('PostalCode')}
                InputProps={{
                  title: 'Bitte füllen Sie dieses Feld aus',
                }}
              />
              <Field
                disabled={isAccessRequested}
                required
                fullWidth
                margin="none"
                component={TextField}
                id="ort"
                name="city"
                label={t('city')}
                InputProps={{
                  title: 'Bitte füllen Sie dieses Feld aus',
                }}
              />
            </Stack>
            <Stack mb={5} direction="row" spacing={8}>
              <Field
                disabled={isAccessRequested}
                fullWidth
                margin="none"
                component={TextField}
                id="e-mail"
                name="email"
                label="E-Mail"
                InputProps={{
                  title: 'Bitte füllen Sie dieses Feld aus',
                }}
                required
              />
              <span style={{ width: '100%' }} aria-hidden></span>
            </Stack>

            <Stack mb={5} direction="row" spacing={8}>
              <Field
                fullWidth
                margin="none"
                component={TextField}
                id="billing-email"
                name="billingEmail"
                label={t('billingEmail')}
                InputProps={{
                  title: 'Bitte füllen Sie dieses Feld aus',
                }}
              />
              <span style={{ width: '100%' }} aria-hidden></span>
            </Stack>

            {/* Billing address toggle */}
            <Stack my={3} direction="row" alignItems="center" spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={values?.useCustomBillingDetails || false}
                    onChange={(e) => {
                      setFieldValue(
                        'useCustomBillingDetails',
                        e.target.checked
                      );
                    }}
                    color="primary"
                  />
                }
                label={t('LBLUseCustomBillingDetails')}
              />
            </Stack>

            {/* Billing fields (shown when billingEnabled) */}
            {(values?.useCustomBillingDetails || false) && (
              <>
                <Stack mb={3} direction="row" spacing={8}>
                  <Field
                    fullWidth
                    margin="none"
                    component={TextField}
                    id="billing-name"
                    name="billingAddressName"
                    label={t('LBLfullName') || 'Name'}
                    required
                  />
                </Stack>

                <Stack mb={3} direction="row" spacing={8}>
                  <Field
                    fullWidth
                    margin="none"
                    component={TextField}
                    id="billing-street"
                    name="billingAddressStreet"
                    label={t('lblStName')}
                    required
                  />
                  <Field
                    fullWidth
                    margin="none"
                    component={TextField}
                    id="billing-careof"
                    name="billingAddressCareOf"
                    label="c/o"
                  />
                </Stack>

                <Stack mb={3} direction="row" spacing={8}>
                  <Field
                    fullWidth
                    required
                    margin="none"
                    component={TextField}
                    id="billing-zip"
                    name="billingAddressZipCode"
                    label={t('PostalCode')}
                  />
                  <Field
                    required
                    fullWidth
                    margin="none"
                    component={TextField}
                    id="billing-city"
                    name="billingAddressCity"
                    label={t('city')}
                  />
                </Stack>
              </>
            )}

            <Button sx={{ display: 'none' }} id="sub" type="submit"></Button>
          </>
        </Form>
      )}
    </Formik>
  );
};

UserCreationForm.propTypes = {
  payload: PropTypes.objectOf(PropTypes.any),
  setPayload: PropTypes.func.isRequired,
  confirmPayload: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default UserCreationForm;
